
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { StorageService } from '../services/storageService';
import { PageSeoConfig } from '../types';
import { useAuth } from './AuthBridge';

interface SEOProps {
  title?: string;
  description?: string;
  schema?: object;
}

/**
 * SEO Component
 * Dynamically updates document metadata, canonical links, and JSON-LD schema.
 * Priority: 1. Specific Path + Active Cert | 2. Specific Path Global | 3. Component Props | 4. Hardcoded Defaults
 */
export const SEO: React.FC<SEOProps> = ({ title: propTitle, description: propDesc, schema }) => {
  const location = useLocation();
  const { activeCert, user } = useAuth();
  const [override, setOverride] = useState<PageSeoConfig | null>(null);

  useEffect(() => {
    const fetchOverride = async () => {
        try {
            // Lookup using active cert context
            const certId = activeCert?.id || 'global';
            const data = await StorageService.getPageSeoConfig(location.pathname, certId);
            if (data) setOverride(data);
        } catch (e) {
            console.warn("SEO Override Registry sync failed.");
        }
    };
    fetchOverride();
  }, [location.pathname, activeCert]);

  useEffect(() => {
    const baseTitle = "Diamond School Of Nursing";
    const certName = activeCert?.name || "Medical";
    const certShort = activeCert?.shortName || "Board";
    const stateName = user?.state || "National";

    // Dynamic Variable Replacement Helper
    const interpolate = (text: string) => {
        if (!text) return text;
        return text
            .replace(/{{certName}}/g, certName)
            .replace(/{{certShortName}}/g, certShort)
            .replace(/{{state}}/g, stateName);
    };
    
    // Resolve content using priority logic
    const finalTitle = override?.title 
        ? `${interpolate(override.title)} | ${baseTitle}` 
        : (propTitle ? `${propTitle} | ${baseTitle}` : `${baseTitle} | Clinical Intelligence Portal`);
    
    const finalDesc = interpolate(override?.description) || propDesc || "Official board preparation platform for medical and nursing certifications featuring board-certified nurse-reviewed study materials and interactive clinical coaching.";
    
    const baseUrl = "https://study.diamondnursingschool.com/practice-exams/";
    
    // 1. Update Document Title
    document.title = finalTitle;

    // 2. Update Meta Description
    const metaDescription = document.getElementById('meta-description');
    if (metaDescription) {
      metaDescription.setAttribute('content', finalDesc);
    }

    // 2b. Update Open Graph & Twitter Cards
    const updateMetaTag = (id: string, attribute: 'name' | 'property', attrValue: string, content: string) => {
      let tag = document.getElementById(id) || document.querySelector(`meta[${attribute}="${attrValue}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.id = id;
        tag.setAttribute(attribute, attrValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', content);
    };

    updateMetaTag('og-title', 'property', 'og:title', finalTitle);
    updateMetaTag('og-description', 'property', 'og:description', finalDesc);
    updateMetaTag('og-url', 'property', 'og:url', `${baseUrl}${location.pathname.replace(/^\/+/, '')}`);
    updateMetaTag('twitter-title', 'name', 'twitter:title', finalTitle);
    updateMetaTag('twitter-description', 'name', 'twitter:description', finalDesc);

    // 3. Manage Canonical URL
    const canonicalId = 'canonical-link';
    let canonicalLink = document.getElementById(canonicalId) as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.id = canonicalId;
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    const cleanPath = location.pathname.replace(/^\/+/, '');
    canonicalLink.setAttribute('href', `${baseUrl}${cleanPath}`);

    // 4. Manage JSON-LD Schema
    const schemaId = 'json-ld-schema';
    let scriptTag = document.getElementById(schemaId) as HTMLScriptElement;
    if (scriptTag) {
      scriptTag.remove();
    }

    // Automatic Breadcrumb Schema Generation
    const pathParts = location.pathname.split('/').filter(Boolean);
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://study.diamondnursingschool.com/"
        },
        ...pathParts.map((part, index) => ({
          "@type": "ListItem",
          "position": index + 2,
          "name": part.charAt(0).toUpperCase() + part.slice(1),
          "item": `${baseUrl}${pathParts.slice(0, index + 1).join('/')}`
        }))
      ]
    };

    // Combine provided schema with breadcrumbs
    let finalSchema: any = schema ? [breadcrumbSchema, schema] : breadcrumbSchema;
    if (override?.jsonLd) {
      try {
        const parsedLd = typeof override.jsonLd === 'string' ? JSON.parse(override.jsonLd) : override.jsonLd;
        if (Array.isArray(finalSchema)) {
          finalSchema.push(parsedLd);
        } else {
          finalSchema = [finalSchema, parsedLd];
        }
      } catch (err) {
        console.warn("Could not parse DB-driven JSON-LD string in SEO component.", err);
      }
    }

    scriptTag = document.createElement('script');
    scriptTag.id = schemaId;
    scriptTag.type = 'application/ld+json';
    scriptTag.text = JSON.stringify(finalSchema);
    document.head.appendChild(scriptTag);

    return () => {
      if (scriptTag) scriptTag.remove();
    };

  }, [propTitle, propDesc, schema, location, override, activeCert, user?.state]);

  return null;
};
